import { addWeeks, differenceInCalendarWeeks, format, parseISO } from 'date-fns'

import APPLICANT_DETAILS_FIXTURE from '../../rental-portal-mock-data/applicant-details.json'
import APPLICANTS_FIXTURE from '../../rental-portal-mock-data/applicants.json'
import DOCUMENTS_FIXTURE from '../../rental-portal-mock-data/documents.json'
import EMAILS_FIXTURE from '../../rental-portal-mock-data/emails.json'
import LISTING_DETAILS_FIXTURE from '../../rental-portal-mock-data/listing-details.json'
import LISTING_UPDATES_FIXTURE from '../../rental-portal-mock-data/listing-updates.json'
import LISTINGS_FIXTURE from '../../rental-portal-mock-data/listings.json'
import PORTAL_PUBLICATIONS_FIXTURE from '../../rental-portal-mock-data/portal-publications.json'
import STAND_LISTINGS_FIXTURE from '../../rental-portal-mock-data/stand-listings.json'
import VIEWING_UPDATES_FIXTURE from '../../rental-portal-mock-data/viewing-updates.json'
import VIEWINGS_FIXTURE from '../../rental-portal-mock-data/viewings.json'
import { ADDRESS_SEGMENT_SEPARATOR } from '../constants/address'
import { EMPTY_STRING } from '../constants/common'
import {
  RENTAL_PORTAL_MOCK_DATE_FORMAT,
  RENTAL_PORTAL_MOCK_DATE_PATTERN,
  RENTAL_PORTAL_MOCK_FIXTURE_WEEK,
  RENTAL_PORTAL_MOCK_WEEK_OPTIONS,
} from '../constants/rentalPortalMockData'

import { getShortAddress } from './address'
import { isValueObjectType, isValueStringType } from './common'

const RENTAL_PORTAL_MOCK_WEEK_SHIFT = differenceInCalendarWeeks(
  new Date(),
  parseISO(RENTAL_PORTAL_MOCK_FIXTURE_WEEK),
  RENTAL_PORTAL_MOCK_WEEK_OPTIONS
)

// TODO: the mock sections point at real stand listings until they are served
const RENTAL_PORTAL_MOCK_STAND_LISTINGS = new Map(
  STAND_LISTINGS_FIXTURE.map(standListing => [
    standListing.mock_id,
    standListing,
  ])
)

function toRentalPortalMockShiftedDate(value) {
  const match = RENTAL_PORTAL_MOCK_DATE_PATTERN.exec(value)

  return match
    ? `${format(
        addWeeks(parseISO(match[1]), RENTAL_PORTAL_MOCK_WEEK_SHIFT),
        RENTAL_PORTAL_MOCK_DATE_FORMAT
      )}${match[2] ?? EMPTY_STRING}`
    : value
}

function toRentalPortalMockShiftedData(value) {
  if (Array.isArray(value)) {
    return value.map(toRentalPortalMockShiftedData)
  }

  if (value && isValueObjectType(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        toRentalPortalMockShiftedData(item),
      ])
    )
  }

  return isValueStringType(value) ? toRentalPortalMockShiftedDate(value) : value
}

function getRentalPortalMockStandUnitLabel({ flat }) {
  return `Top ${flat.number}${ADDRESS_SEGMENT_SEPARATOR}${getShortAddress(flat.building.address.value)}`
}

function toRentalPortalMockStandListing(listing) {
  const standListing = RENTAL_PORTAL_MOCK_STAND_LISTINGS.get(listing.id)

  return standListing
    ? {
        ...listing,
        flat: {
          ...listing.flat,
          ...standListing.flat,
          building: {
            ...listing.flat.building,
            ...standListing.flat.building,
            address: {
              ...listing.flat.building.address,
              ...standListing.flat.building.address,
            },
          },
        },
        id: standListing.id,
        listing_title: standListing.listing_title,
        unit_label: getRentalPortalMockStandUnitLabel(standListing),
      }
    : listing
}

function toRentalPortalMockStandReference(item) {
  const standListing = RENTAL_PORTAL_MOCK_STAND_LISTINGS.get(item.listing_id)

  return standListing
    ? {
        ...item,
        listing_id: standListing.id,
        ...(item.listing_label
          ? { listing_label: getRentalPortalMockStandUnitLabel(standListing) }
          : null),
      }
    : item
}

export const APPLICANT_DETAILS_SNAPSHOT = toRentalPortalMockShiftedData(
  APPLICANT_DETAILS_FIXTURE
)

export const APPLICANTS_SNAPSHOT = toRentalPortalMockShiftedData(
  APPLICANTS_FIXTURE
).map(toRentalPortalMockStandReference)

export const DOCUMENTS_SNAPSHOT = toRentalPortalMockShiftedData(
  DOCUMENTS_FIXTURE
).map(toRentalPortalMockStandReference)

export const EMAILS_SNAPSHOT = toRentalPortalMockShiftedData(
  EMAILS_FIXTURE
).map(toRentalPortalMockStandReference)

export const LISTING_DETAILS_SNAPSHOT = toRentalPortalMockShiftedData(
  LISTING_DETAILS_FIXTURE
).map(toRentalPortalMockStandListing)

export const LISTING_UPDATES_SNAPSHOT = toRentalPortalMockShiftedData(
  LISTING_UPDATES_FIXTURE
).map(toRentalPortalMockStandReference)

export const LISTINGS_SNAPSHOT = toRentalPortalMockShiftedData(
  LISTINGS_FIXTURE
).map(toRentalPortalMockStandListing)

export const PORTAL_PUBLICATIONS_SNAPSHOT = toRentalPortalMockShiftedData(
  PORTAL_PUBLICATIONS_FIXTURE
)

export const VIEWING_UPDATES_SNAPSHOT = toRentalPortalMockShiftedData(
  VIEWING_UPDATES_FIXTURE
)

export const VIEWINGS_SNAPSHOT = toRentalPortalMockShiftedData(
  VIEWINGS_FIXTURE
).map(toRentalPortalMockStandReference)
